import styled from '@emotion/styled';
import {
  Button as _Button,
  CardContent as _CardContent,
  Dialog,
  FormControlLabel as _FormControlLabel,
  Grid,
  List as _List,
  ListItem as _ListItem,
  ListItemIcon as _ListItemIcon,
  ListItemText,
  MenuItem as _MenuItem,
  Paper as _Paper,
  Radio as _Radio,
  Select,
  Slide,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { createElement, FC, MouseEvent, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Slider from 'react-slick';

import { BusinessRegionAdRegionKey, DesignInspirationCategoryDetailFragment } from '../../../generated/graphql';
import theme from '../../../theme';
import MediaWithSpFrame from '../../component/compound/MediaWithSpFrame';
import Spinner from '../../component/compound/Spinner';
import ImageScreen from '../../component/primitive/ImageScreen';
import VideoScreen from '../../component/primitive/VideoScreen';
import { SCREEN_LARGE } from '../../constant/pageWidth';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import mq from '../../styleUtility/mediaquery';

type Props = {
  loading: boolean;
  catgories: Array<DesignInspirationCategoryDetailFragment>;
  onClickBackButton: MouseEventHandler<HTMLElement>;
  onClickNextButton: ({ designInspirationCaseId }: { designInspirationCaseId: string }) => void;
  open: boolean;
  region: BusinessRegionAdRegionKey;
  className?: string;
};

type CategoryData = {
  id: string;
  name: string;
  iconUrl: string;
  selected: boolean;
  videos: Array<Video>;
};

type Video = {
  id: string;
  name: string;
  description: string;
  src: string;
  selected: boolean;
};

type DesignInspirationCategoryHooks = [
  Array<CategoryData>,
  {
    setSelectedVideo: (id: string, selected: boolean) => void;
    setSelectedCategory: (categoryId: string) => void;
    updateVideo: (videoData: Array<CategoryData>) => void;
  },
];

const useDesignInspirationCategory = (videoData: Array<CategoryData>): DesignInspirationCategoryHooks => {
  const [videos, setVideos] = useState(videoData);

  useEffect(() => {
    setSelectedCategory(videos[0] ? videos[0].id : '');
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos]);

  const selectedVideoData = (id: string, selected: boolean) =>
    videos.map((video) => ({
      ...video,
      videos: video.videos.map((videoItem) => {
        if (videoItem.id === id) {
          videoItem.selected = selected;
        } else {
          videoItem.selected = false;
        }
        return videoItem;
      }),
    }));

  const selectedCategory = (categoryId: string) =>
    videos.map((video) => {
      if (video.id === categoryId) {
        video.selected = true;
      } else {
        video.selected = false;
      }
      return video;
    });

  const setSelectedVideo = (id: string, selected: boolean): void => {
    setVideos(selectedVideoData(id, selected));
  };

  const setSelectedCategory = (categoryId: string): void => {
    setVideos(selectedCategory(categoryId));
  };

  const updateVideo = (videoData: Array<CategoryData>): void => {
    setVideos(videoData);
  };

  return [videos, { setSelectedVideo, setSelectedCategory, updateVideo }];
};

function Transition(props: any) {
  return <Slide direction="up" {...props} />;
}

const DesignInspirationsListPage: FC<Props> = ({
  open,
  region,
  loading,
  catgories,
  onClickBackButton,
  onClickNextButton,
  className,
}) => {
  const isPC = useMediaQuery(`(min-width:${theme.breakpoints.values['lg']}px)`);

  // : Array<CategoryData>
  const videoData = catgories.map((category) => {
    return {
      id: category.id,
      selected: false,
      name: category.name,
      iconUrl: category.iconUrl,
      videos: category.designInspirationCases.map((inspiration) => {
        return {
          id: inspiration.id,
          name: inspiration.name,
          description: inspiration.description || '',
          src: inspiration.url,
          selected: false,
        };
      }),
    };
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [videos, setVideo] = useDesignInspirationCategory(videoData);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const videosBySelectedCategory = videos.filter((videos) => videos.selected);
  const isDisabled = !videosBySelectedCategory.some((videoData) => videoData.videos.some((video) => video.selected));

  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    setVideo.updateVideo(videoData);
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoData.length, videoData[0].id]);

  useEffect(() => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  }, [selectedIndex]);
  const handleListItemClick = (categoryId: string, index: number) => {
    setSelectedIndex(index);
    setVideo.setSelectedCategory(categoryId);
  };

  const handleSelectVideoCase = (id: string, selected: boolean) => {
    setVideo.setSelectedVideo(id, selected);
    setSelectedCaseId(id);
  };

  const handleCloseModal = () => {
    setSelectedIndex(0);
    setVideo.setSelectedCategory(videos[0] ? videos[0].id : '');
  };

  if (loading) {
    return <Spinner />;
  }

  const carouselSize =
    videosBySelectedCategory.length > 0
      ? videosBySelectedCategory[0].videos.length < 3
        ? videosBySelectedCategory[0].videos.length
        : 3
      : 0;

  return (
    <Dialog open={open} fullScreen={isPC ? false : true} maxWidth={isPC ? 'lg' : 'xl'} TransitionComponent={Transition}>
      <Header>
        <HeaderButton
          onClick={(e: any) => {
            onClickBackButton(e);
            handleCloseModal();
          }}
        >
          {isPC ? <CloseIcon /> : ''}
        </HeaderButton>
      </Header>
      <Root className={className}>
        <Paper>
          <Grid container direction={isPC ? 'row' : 'column'} justify="center" alignItems="stretch">
            <MenuGrid item sm={12} md={3} lg={3}>
              <Label variant="body2">
                {region === 'us' ? (
                  <FormattedMessage id="us_design_inspiration_page.category_description" />
                ) : (
                  <FormattedMessage id="design_inspiration_page.category_description" />
                )}
              </Label>
              {isPC ? (
                <Lists component="nav">
                  {videos.map((category, index) => (
                    <ListItem
                      button
                      component="a"
                      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                        handleListItemClick(e.currentTarget.attributes['data-category' as any].value as string, index);
                      }}
                      data-category={category.id}
                      key={index}
                      style={{
                        border: 'none',
                      }}
                    >
                      <ListItemWrapper>
                        <LiteItemContent>
                          <ListItemIcon>
                            <CategoryIcon src={category.iconUrl} isSelected={selectedIndex === index} />
                          </ListItemIcon>
                          <CategoryText
                            primary={category.name}
                            primaryTypographyProps={{
                              style: {
                                color: selectedIndex === index ? theme.palette.primary.main : '#4C566C',
                              },
                            }}
                          />
                        </LiteItemContent>
                      </ListItemWrapper>
                    </ListItem>
                  ))}
                </Lists>
              ) : (
                <Selects
                  id="select"
                  value={videos[selectedIndex].name ?? 'App'}
                  onChange={(e) => {
                    const target = e.currentTarget as HTMLSelectElement;
                    const categoryId = target.attributes['data-category' as any].value as string;
                    const idx = Number(target.attributes['data-index' as any].value);
                    handleListItemClick(categoryId, idx);
                  }}
                >
                  {[
                    ...videos.map((category, index) => (
                      <MenuItem data-category={category.id} data-index={index} key={index} value={category.name}>
                        <LiteItemContent>
                          <ListItemIcon>
                            <CategoryIcon src={category.iconUrl} isSelected={selectedIndex === index} />
                          </ListItemIcon>
                          <CategoryText
                            primary={category.name}
                            primaryTypographyProps={{
                              style: {
                                color: selectedIndex === index ? theme.palette.primary.main : '#4C566C',
                              },
                            }}
                          />
                        </LiteItemContent>
                      </MenuItem>
                    )),
                  ]}
                </Selects>
              )}
            </MenuGrid>
            <VideoGrid item sm={12} md={9} lg={9}>
              <VideoLists>
                <Label variant="body2">
                  {region === 'us' ? (
                    <FormattedMessage id="us_design_inspiration_page.video_description" />
                  ) : (
                    <FormattedMessage id="design_inspiration_page.video_description" />
                  )}
                </Label>
                {isPC ? (
                  <Slider dots speed={500} slidesToShow={carouselSize} slidesToScroll={carouselSize} ref={sliderRef}>
                    {renderVideoContents({ videos: videosBySelectedCategory, setVideo: handleSelectVideoCase })}
                  </Slider>
                ) : (
                  <VideoWrapper container direction="row" justify="center" alignItems="stretch" spacing={2}>
                    {renderVideoContents({ videos: videosBySelectedCategory, setVideo: handleSelectVideoCase })}
                  </VideoWrapper>
                )}
              </VideoLists>
            </VideoGrid>
          </Grid>
        </Paper>
        <Actions>
          <Buttons>
            <Button
              disabled={isDisabled}
              onClick={() => onClickNextButton({ designInspirationCaseId: selectedCaseId })}
            >
              <FormattedMessage id="design_inspiration_page.buttons.submit" />
            </Button>
            {!isPC ? (
              <Button
                color="default"
                onClick={(e: any) => {
                  onClickBackButton(e);
                  handleCloseModal();
                }}
              >
                <FormattedMessage id="design_inspiration_page.buttons.close" />
              </Button>
            ) : (
              ''
            )}
          </Buttons>
        </Actions>
      </Root>
    </Dialog>
  );
};

const renderVideoContents = ({
  videos,
  setVideo,
}: {
  videos: Array<CategoryData>;
  setVideo: (id: string, selected: boolean) => void;
}) =>
  videos.length
    ? videos[0].videos.map((video) => (
        <VideoContents item sm={6} md={4} lg={4} key={video.id}>
          <CardContent>
            <MediaWithSpFrame>
              <Video src={video.src} autoPlay loop muted playsInline nodownload preloadAttr="metadata" />
            </MediaWithSpFrame>
          </CardContent>
          <FormControlLabel
            label={<VideoName variant="subtitle2">{video.name}</VideoName>}
            control={
              <Radio
                checked={video.selected}
                onChange={() => setVideo(video.id, !video.selected)}
                value={video.id}
                color="primary"
              />
            }
          />
        </VideoContents>
      ))
    : null;

const Root = styled('section')({
  width: 'auto',
  maxWidth: SCREEN_LARGE,
  marginRight: 'auto',
  marginLeft: 'auto',
  paddingBottom: SPACING_UNIT * 12,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    marginTop: 0,
    padding: SPACING_REGULAR,
    paddingTop: 0,
  },
});

const Header = styled('div')({
  display: 'flex',
  padding: '8px',
  minHeight: '40px',
  justifyContent: 'flex-end',
});

const HeaderButton = styled('div')({
  display: 'inline-block',
  cursor: 'pointer',
  '&:hover': {
    background: 'rgb(238, 238, 238);',
  },
});

const Paper = styled(_Paper)({
  padding: '16px',
  paddingTop: 0,
  border: 'none',
  [theme.breakpoints.down('md')]: {
    padding: 0,
  },
});

const MenuGrid = styled(Grid)({
  [theme.breakpoints.down('lg')]: {
    maxWidth: '100%',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: '0 0 16px',
  },
});

const Selects = styled(Select)({
  width: '100%',
});

const MenuItem = styled(_MenuItem)({
  display: 'flex',
});

const Lists = (styled(_List)({
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    flexWrap: 'wrap',
  },
}) as unknown) as typeof _List;

const ListItem = (styled(_ListItem)({
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    flexDirection: 'column',
    width: '27%',
    maxWidth: '100%',
    height: 'auto',
    margin: '3%',
    padding: '8px',
    position: 'relative',
    border: '1px solid rgba(0,0,0,0.3)',
    borderRadius: '6px',
    '::before': {
      content: '""',
      display: 'block',
      paddingTop: '100%',
    },
  },
}) as unknown) as typeof _ListItem;

const ListItemWrapper = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
  {
    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
);

const LiteItemContent = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
  {
    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
      margin: 'auto 0',
    },
  },
);

const ListItemIcon = styled(_ListItemIcon)({
  [theme.breakpoints.down('md')]: {
    minWidth: 'auto',
  },
});

const CategoryIcon = styled(ImageScreen)<{ isSelected: boolean }>(({ isSelected }) => ({
  width: 24,
  height: 24,
  background: 'transparent',
  filter: isSelected ? 'invert(39%) sepia(34%) saturate(5761%) hue-rotate(141deg) brightness(98%) contrast(102%)' : '',
  [theme.breakpoints.down('md')]: {
    width: '32px',
    height: '32px',
  },
}));

const CategoryText = styled(ListItemText)({
  fontWeight: 'bold',
  [theme.breakpoints.down('md')]: {
    marginLeft: '20px',
    textAlign: 'left',
    fontSize: '14px',
    lineHeight: '1.1',
  },
});

const Label = styled(Typography)({
  color: '#4C566C',
  fontWeight: 'bold',
  marginBottom: SPACING_LARGE,
  [theme.breakpoints.down('md')]: {
    marginBottom: SPACING_UNIT,
  },
});

const VideoGrid = styled(Grid)({
  width: '960px',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    maxWidth: '100%',
  },
});

const VideoWrapper = styled(Grid)({
  overflowX: 'scroll',
  flexWrap: 'nowrap',
  justifyContent: 'normal',

  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    padding: '16px 0',
  },
});

const VideoContents = styled(Grid)({
  width: '100%',
  maxWidth: '100%',
  outline: 'none',
  padding: '16px',
  [theme.breakpoints.down('md')]: {
    width: '33%',
  },
});

const VideoLists = styled('div')({
  padding: SPACING_REGULAR,
  paddingTop: 0,
  borderLeft: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    minWidth: 'auto',
    padding: `${SPACING_REGULAR}px 0px`,
    border: 'none',
  },
});

const Video = styled(VideoScreen)`
  width: 100%;
  height: 100%;
`;

const CardContent = styled(_CardContent)(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
  {
    padding: 0,
  },
);

const Actions = styled('div')(
  flexibleLayoutContainer({
    justifyContent: 'flex-end',
  }),
  { marginTop: SPACING_LARGE },
);

const Buttons = styled('div')({
  padding: '0 16px',
  'button + button': {
    marginLeft: SPACING_UNIT * 2,
  },
  [mq('sp')]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginTop: SPACING_REGULAR,
    '> button': {
      width: '100%',
      marginBottom: SPACING_UNIT,
      '&:last-of-type': {
        marginLeft: 0,
      },
    },
  },
});

const Button = styled(_Button)({
  width: 160,
});

const Radio = styled(_Radio)({
  padding: 0,
  marginRight: '8px',
  [theme.breakpoints.down('md')]: {
    marginRight: '4px',
  },
});

const VideoName = styled(Typography)({
  lineHeight: '1.1',
  paddingRight: '8px',
  [theme.breakpoints.down('md')]: {
    padding: '0',
  },
});

const FormControlLabel = styled(_FormControlLabel)({
  margin: 0,
  wordBreak: 'break-all',
  padding: '8px 0',
  [theme.breakpoints.down('md')]: {
    padding: '4px 0',
  },
});
export default DesignInspirationsListPage;
